import React, { useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import {
  Users,
  Shield,
  Plus,
  Loader2,
  Trash2,
  Edit,
  Mail,
  UserX,
  UserCheck,
  RefreshCw,
  AlertTriangle,
  XCircle,
  RotateCcw,
  Key,
} from 'lucide-react';
import { toast } from 'sonner';
import { getRoleBadge } from './adminUtils';

const UsersTab = ({ api, currentUser, users, fetchData }) => {
  // Dialog states
  const [showNewUserDialog, setShowNewUserDialog] = useState(false);
  const [showInviteUserDialog, setShowInviteUserDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [showSetPasswordDialog, setShowSetPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  // Form states
  const [newUser, setNewUser] = useState({
    email: '',
    full_name: '',
    phone: '',
    password: '',
    role: 'staff',
    is_admin: false,
    force_password_reset: false,  // Default: account ready to use immediately
  });
  const [inviteUser, setInviteUser] = useState({
    email: '',
    full_name: '',
    phone: '',
    role: 'staff',
    is_admin: false,
  });
  const [editUser, setEditUser] = useState({
    full_name: '',
    phone: '',
    role: 'staff',
    is_admin: false,
    is_active: true,
  });
  const [userPermissions, setUserPermissions] = useState({
    view_all_calendars: false,
    edit_clients: false,
    edit_bookings: false,
    assign_jobs: false,
    override_conflicts: false,
    view_audit_logs: false,
    manage_users: false,
    view_financials: false,
    taco_admin: false,
    taco_conversation_review: false,
  });

  // User handlers
  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.full_name || !newUser.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    setCreating(true);
    try {
      await api().post('/users', newUser);
      toast.success('User created successfully!');
      setShowNewUserDialog(false);
      setNewUser({ email: '', full_name: '', phone: '', password: '', role: 'staff', is_admin: false, force_password_reset: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditUser({
      full_name: user.full_name || '',
      phone: user.phone || '',
      role: user.role || 'staff',
      is_admin: user.is_admin || false,
      is_active: user.is_active !== false,
    });
    setShowEditUserDialog(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    setCreating(true);
    try {
      await api().patch(`/users/${selectedUser.id}`, editUser);
      toast.success('User updated successfully!');
      setShowEditUserDialog(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    if (selectedUser.id === currentUser?.id) {
      toast.error("You cannot delete your own account");
      return;
    }
    setCreating(true);
    try {
      await api().delete(`/users/${selectedUser.id}?confirm=true`);
      toast.success('User deleted successfully!');
      setShowDeleteUserDialog(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setCreating(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteUser.email || !inviteUser.full_name) {
      toast.error('Please fill in email and name');
      return;
    }
    setCreating(true);
    try {
      await api().post('/users/invite', inviteUser);
      toast.success('Invite sent! User will receive an email to set their password.');
      setShowInviteUserDialog(false);
      setInviteUser({ email: '', full_name: '', phone: '', role: 'staff', is_admin: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setCreating(false);
    }
  };

  const handleResendInvite = async (userId) => {
    try {
      await api().post(`/users/${userId}/resend-invite`);
      toast.success('Invite email resent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invite');
    }
  };

  const handleDeactivateUser = async () => {
    if (!selectedUser) return;
    setCreating(true);
    try {
      await api().post(`/users/${selectedUser.id}/deactivate`);
      toast.success('User deactivated');
      setShowDeactivateDialog(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate user');
    } finally {
      setCreating(false);
    }
  };

  const handleReactivateUser = async (userId) => {
    try {
      await api().post(`/users/${userId}/reactivate`);
      toast.success('User reactivated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate user');
    }
  };

  const handleRevokeInvite = async (userId) => {
    try {
      await api().post(`/users/${userId}/revoke-invite`);
      toast.success('Invite revoked');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke invite');
    }
  };

  const handleSetPassword = async () => {
    if (!selectedUser || !newPassword) {
      toast.error('Please enter a password');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setCreating(true);
    try {
      await api().post(`/users/${selectedUser.id}/set-password`, { new_password: newPassword });
      toast.success(`Password set for ${selectedUser.email}. They can now login immediately.`);
      setShowSetPasswordDialog(false);
      setSelectedUser(null);
      setNewPassword('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setCreating(false);
    }
  };

  const openSetPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowSetPasswordDialog(true);
  };

  const handleEditPermissions = (user) => {
    setSelectedUser(user);
    setUserPermissions(user.permissions || {
      view_all_calendars: false,
      edit_clients: false,
      edit_bookings: false,
      assign_jobs: false,
      override_conflicts: false,
      view_audit_logs: false,
      manage_users: false,
      view_financials: false,
      taco_admin: false,
      taco_conversation_review: false,
    });
    setShowPermissionsDialog(true);
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;
    setCreating(true);
    try {
      await api().patch(`/users/${selectedUser.id}`, { permissions: userPermissions });
      toast.success('Permissions updated');
      setShowPermissionsDialog(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setCreating(false);
    }
  };

  const handleResetPermissions = async () => {
    if (!selectedUser) return;
    setCreating(true);
    try {
      const response = await api().post(`/users/${selectedUser.id}/reset-permissions`);
      toast.success(response.data.message || 'Permissions reset to defaults');
      setUserPermissions(response.data.permissions);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset permissions');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">Team Members</h2>
          <p className="text-sm text-zinc-500">{users.length} user{users.length !== 1 ? 's' : ''} registered</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowInviteUserDialog(true)} data-testid="invite-user-btn">
            <Mail className="mr-2 h-4 w-4" />
            Invite User
          </Button>
          <Button onClick={() => setShowNewUserDialog(true)} data-testid="add-user-btn">
            <Plus className="mr-2 h-4 w-4" />
            Add User
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {users.map((user) => (
          <Card key={user.id} className={`border-zinc-100 hover:border-zinc-200 transition-colors ${!user.is_active ? 'opacity-60' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`h-12 w-12 rounded-full flex items-center justify-center text-white font-medium text-lg ${user.is_active ? 'bg-zinc-900' : 'bg-zinc-400'}`}>
                    {user.full_name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-zinc-900">{user.full_name}</p>
                      <Badge className={getRoleBadge(user.role)}>{user.role}</Badge>
                      {user.is_admin && (
                        <Badge className="bg-amber-50 text-amber-700">
                          <Shield className="mr-1 h-3 w-3" />
                          Admin
                        </Badge>
                      )}
                      {!user.is_active && (
                        <Badge className="bg-red-50 text-red-700">
                          <UserX className="mr-1 h-3 w-3" />
                          Inactive
                        </Badge>
                      )}
                      {user.invite_sent_at && user.force_password_reset && (
                        <Badge className="bg-blue-50 text-blue-700">
                          <Mail className="mr-1 h-3 w-3" />
                          Invite Pending
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-zinc-500">{user.email}</p>
                    {user.phone && <p className="text-xs text-zinc-400">{user.phone}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap justify-end">
                  {user.invite_sent_at && user.force_password_reset && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => handleResendInvite(user.id)} title="Resend invite email">
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleRevokeInvite(user.id)} title="Revoke invite">
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => handleEditPermissions(user)} title="Edit permissions">
                    <Shield className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openSetPasswordDialog(user)} title="Set password" data-testid={`set-password-${user.id}`}>
                    <Key className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleEditUser(user)} data-testid={`edit-user-${user.id}`} title="Edit user">
                    <Edit className="h-4 w-4" />
                  </Button>
                  {user.id !== currentUser?.id && (
                    user.is_active ? (
                      <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700" onClick={() => { setSelectedUser(user); setShowDeactivateDialog(true); }} title="Deactivate user">
                        <UserX className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700" onClick={() => handleReactivateUser(user.id)} title="Reactivate user">
                        <UserCheck className="h-4 w-4" />
                      </Button>
                    )
                  )}
                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => { setSelectedUser(user); setShowDeleteUserDialog(true); }} disabled={user.id === currentUser?.id} data-testid={`delete-user-${user.id}`} title="Delete user">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {users.length === 0 && (
          <Card className="border-zinc-100">
            <CardContent className="p-8 text-center">
              <Users className="h-12 w-12 mx-auto mb-4 text-zinc-300" />
              <p className="text-zinc-500">No team members yet</p>
              <div className="flex gap-2 justify-center mt-4">
                <Button variant="outline" onClick={() => setShowInviteUserDialog(true)}>
                  <Mail className="mr-2 h-4 w-4" />
                  Invite User
                </Button>
                <Button onClick={() => setShowNewUserDialog(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add User
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* New User Dialog */}
      <Dialog open={showNewUserDialog} onOpenChange={setShowNewUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Create a new team member account. By default, they can login immediately with the password you set.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="new-email">Email *</Label>
              <Input id="new-email" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="mt-2" data-testid="new-user-email" />
            </div>
            <div>
              <Label htmlFor="new-name">Full Name *</Label>
              <Input id="new-name" value={newUser.full_name} onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })} className="mt-2" data-testid="new-user-name" />
            </div>
            <div>
              <Label htmlFor="new-phone">Phone</Label>
              <Input id="new-phone" value={newUser.phone} onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })} className="mt-2" data-testid="new-user-phone" />
            </div>
            <div>
              <Label htmlFor="new-password">Password *</Label>
              <Input id="new-password" type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="mt-2" data-testid="new-user-password" />
            </div>
            <div>
              <Label htmlFor="new-role">Role</Label>
              <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                <SelectTrigger className="mt-2" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="director">Director</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="new-admin" checked={newUser.is_admin} onChange={(e) => setNewUser({ ...newUser, is_admin: e.target.checked })} className="rounded" data-testid="new-user-admin" />
              <Label htmlFor="new-admin">Grant admin privileges</Label>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="force-reset" checked={newUser.force_password_reset} onChange={(e) => setNewUser({ ...newUser, force_password_reset: e.target.checked })} className="rounded" data-testid="new-user-force-reset" />
              <Label htmlFor="force-reset" className="text-sm text-zinc-600">Require password change on first login</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewUserDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateUser} disabled={creating} data-testid="create-user-btn">
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUserDialog} onOpenChange={(open) => { setShowEditUserDialog(open); if (!open) setSelectedUser(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user details and permissions for {selectedUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-name">Full Name</Label>
              <Input id="edit-name" value={editUser.full_name} onChange={(e) => setEditUser({ ...editUser, full_name: e.target.value })} className="mt-2" data-testid="edit-user-name" />
            </div>
            <div>
              <Label htmlFor="edit-phone">Phone</Label>
              <Input id="edit-phone" value={editUser.phone} onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })} className="mt-2" data-testid="edit-user-phone" />
            </div>
            <div>
              <Label htmlFor="edit-role">Role</Label>
              <Select value={editUser.role} onValueChange={(value) => setEditUser({ ...editUser, role: value })}>
                <SelectTrigger className="mt-2" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="director">Director</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <Label>Admin Privileges</Label>
                <p className="text-sm text-zinc-500">Can access admin portal</p>
              </div>
              <Switch checked={editUser.is_admin} onCheckedChange={(checked) => setEditUser({ ...editUser, is_admin: checked })} data-testid="edit-user-admin" />
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <Label>Account Status</Label>
                <p className="text-sm text-zinc-500">Enable or disable login</p>
              </div>
              <Switch checked={editUser.is_active} onCheckedChange={(checked) => setEditUser({ ...editUser, is_active: checked })} data-testid="edit-user-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditUserDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateUser} disabled={creating} data-testid="update-user-btn">
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Password Dialog */}
      <Dialog open={showSetPasswordDialog} onOpenChange={(open) => { setShowSetPasswordDialog(open); if (!open) { setSelectedUser(null); setNewPassword(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Password</DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUser?.full_name}. They will be able to login immediately with this password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedUser && (
              <div className="flex items-center gap-4 p-4 bg-zinc-50 rounded-lg">
                <div className="h-12 w-12 rounded-full bg-zinc-900 flex items-center justify-center text-white font-medium text-lg">
                  {selectedUser.full_name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="font-medium text-zinc-900">{selectedUser.full_name}</p>
                  <p className="text-sm text-zinc-500">{selectedUser.email}</p>
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="set-password">New Password *</Label>
              <Input 
                id="set-password" 
                type="password" 
                value={newPassword} 
                onChange={(e) => setNewPassword(e.target.value)} 
                placeholder="Enter new password (min 6 characters)"
                className="mt-2" 
                data-testid="set-password-input" 
              />
            </div>
            {selectedUser?.force_password_reset && (
              <p className="text-sm text-amber-600">
                This user currently has a pending password reset requirement. Setting a password here will clear that and allow them to login immediately.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetPasswordDialog(false)}>Cancel</Button>
            <Button onClick={handleSetPassword} disabled={creating || !newPassword} data-testid="confirm-set-password-btn">
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Set Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={showDeleteUserDialog} onOpenChange={(open) => { setShowDeleteUserDialog(open); if (!open) setSelectedUser(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete User</DialogTitle>
            <DialogDescription>Are you sure you want to delete this user? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="py-4">
              <div className="flex items-center gap-4 p-4 bg-zinc-50 rounded-lg">
                <div className="h-12 w-12 rounded-full bg-zinc-900 flex items-center justify-center text-white font-medium text-lg">
                  {selectedUser.full_name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="font-medium text-zinc-900">{selectedUser.full_name}</p>
                  <p className="text-sm text-zinc-500">{selectedUser.email}</p>
                  <Badge className={getRoleBadge(selectedUser.role)}>{selectedUser.role}</Badge>
                </div>
              </div>
              <p className="text-sm text-red-600 mt-4">
                All data associated with this user (bookings, audit logs) will be preserved but the user will no longer be able to log in.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteUserDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={creating} data-testid="confirm-delete-user-btn">
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={showInviteUserDialog} onOpenChange={setShowInviteUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Invite New User
            </DialogTitle>
            <DialogDescription>
              Send an invitation email with a secure link to set their password. The link expires after 48 hours.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="invite-email">Email *</Label>
              <Input id="invite-email" type="email" value={inviteUser.email} onChange={(e) => setInviteUser({ ...inviteUser, email: e.target.value })} className="mt-2" data-testid="invite-user-email" />
            </div>
            <div>
              <Label htmlFor="invite-name">Full Name *</Label>
              <Input id="invite-name" value={inviteUser.full_name} onChange={(e) => setInviteUser({ ...inviteUser, full_name: e.target.value })} className="mt-2" data-testid="invite-user-name" />
            </div>
            <div>
              <Label htmlFor="invite-phone">Phone</Label>
              <Input id="invite-phone" value={inviteUser.phone} onChange={(e) => setInviteUser({ ...inviteUser, phone: e.target.value })} className="mt-2" data-testid="invite-user-phone" />
            </div>
            <div>
              <Label htmlFor="invite-role">Role</Label>
              <Select value={inviteUser.role} onValueChange={(value) => setInviteUser({ ...inviteUser, role: value })}>
                <SelectTrigger className="mt-2" data-testid="invite-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="director">Director</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="invite-admin" checked={inviteUser.is_admin} onChange={(e) => setInviteUser({ ...inviteUser, is_admin: e.target.checked })} className="rounded" data-testid="invite-user-admin" />
              <Label htmlFor="invite-admin">Grant admin privileges</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteUserDialog(false)}>Cancel</Button>
            <Button onClick={handleInviteUser} disabled={creating} data-testid="send-invite-btn">
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Mail className="mr-2 h-4 w-4" />
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate User Confirmation Dialog */}
      <Dialog open={showDeactivateDialog} onOpenChange={(open) => { setShowDeactivateDialog(open); if (!open) setSelectedUser(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Deactivate User
            </DialogTitle>
            <DialogDescription>
              Deactivating a user will prevent them from logging in, but preserves their job history and data.
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="py-4">
              <div className="flex items-center gap-4 p-4 bg-zinc-50 rounded-lg">
                <div className="h-12 w-12 rounded-full bg-zinc-900 flex items-center justify-center text-white font-medium text-lg">
                  {selectedUser.full_name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="font-medium text-zinc-900">{selectedUser.full_name}</p>
                  <p className="text-sm text-zinc-500">{selectedUser.email}</p>
                  <Badge className={getRoleBadge(selectedUser.role)}>{selectedUser.role}</Badge>
                </div>
              </div>
              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800 flex items-center gap-2">
                  <UserX className="h-4 w-4" />
                  This user will no longer be able to log in or appear in staff assignment lists.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeactivateDialog(false)}>Cancel</Button>
            <Button variant="default" className="bg-amber-600 hover:bg-amber-700" onClick={handleDeactivateUser} disabled={creating} data-testid="confirm-deactivate-user-btn">
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Deactivate User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={showPermissionsDialog} onOpenChange={(open) => { setShowPermissionsDialog(open); if (!open) setSelectedUser(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Edit Permissions
            </DialogTitle>
            <DialogDescription>
              {selectedUser?.full_name} ({selectedUser?.role}) - Configure granular permissions for this user.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4 max-h-[400px] overflow-y-auto">
            {/* Calendar & Viewing */}
            <div>
              <h4 className="text-sm font-medium text-zinc-700 mb-3">Calendar & Viewing</h4>
              <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                <div>
                  <Label className="text-sm">View All Calendars</Label>
                  <p className="text-xs text-zinc-500">Can see all staff schedules</p>
                </div>
                <Switch checked={userPermissions.view_all_calendars} onCheckedChange={(checked) => setUserPermissions({ ...userPermissions, view_all_calendars: checked })} data-testid="perm-view-calendars" />
              </div>
            </div>

            {/* Client & Booking Management */}
            <div>
              <h4 className="text-sm font-medium text-zinc-700 mb-3">Client & Booking Management</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                  <div>
                    <Label className="text-sm">Edit Clients</Label>
                    <p className="text-xs text-zinc-500">Create, update, delete clients</p>
                  </div>
                  <Switch checked={userPermissions.edit_clients} onCheckedChange={(checked) => setUserPermissions({ ...userPermissions, edit_clients: checked })} data-testid="perm-edit-clients" />
                </div>
                <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                  <div>
                    <Label className="text-sm">Edit Bookings</Label>
                    <p className="text-xs text-zinc-500">Create, update, cancel bookings</p>
                  </div>
                  <Switch checked={userPermissions.edit_bookings} onCheckedChange={(checked) => setUserPermissions({ ...userPermissions, edit_bookings: checked })} data-testid="perm-edit-bookings" />
                </div>
                <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                  <div>
                    <Label className="text-sm">Assign Jobs</Label>
                    <p className="text-xs text-zinc-500">Assign staff to bookings</p>
                  </div>
                  <Switch checked={userPermissions.assign_jobs} onCheckedChange={(checked) => setUserPermissions({ ...userPermissions, assign_jobs: checked })} data-testid="perm-assign-jobs" />
                </div>
                <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                  <div>
                    <Label className="text-sm">Override Conflicts</Label>
                    <p className="text-xs text-zinc-500">Force assignments with schedule conflicts</p>
                  </div>
                  <Switch checked={userPermissions.override_conflicts} onCheckedChange={(checked) => setUserPermissions({ ...userPermissions, override_conflicts: checked })} data-testid="perm-override-conflicts" />
                </div>
              </div>
            </div>

            {/* Admin & Audit */}
            <div>
              <h4 className="text-sm font-medium text-zinc-700 mb-3">Admin & Audit</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                  <div>
                    <Label className="text-sm">View Audit Logs</Label>
                    <p className="text-xs text-zinc-500">Access activity history</p>
                  </div>
                  <Switch checked={userPermissions.view_audit_logs} onCheckedChange={(checked) => setUserPermissions({ ...userPermissions, view_audit_logs: checked })} data-testid="perm-view-audit" />
                </div>
                <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                  <div>
                    <Label className="text-sm">Manage Users</Label>
                    <p className="text-xs text-zinc-500">Create, edit, deactivate users</p>
                  </div>
                  <Switch checked={userPermissions.manage_users} onCheckedChange={(checked) => setUserPermissions({ ...userPermissions, manage_users: checked })} data-testid="perm-manage-users" />
                </div>
              </div>
            </div>

            {/* Financial */}
            <div>
              <h4 className="text-sm font-medium text-zinc-700 mb-3">Financial</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                  <div>
                    <Label className="text-sm">View Financials</Label>
                    <p className="text-xs text-zinc-500">See job values and revenue tiles</p>
                  </div>
                  <Switch checked={userPermissions.view_financials} onCheckedChange={(checked) => setUserPermissions({ ...userPermissions, view_financials: checked })} data-testid="perm-view-financials" />
                </div>
              </div>
            </div>

            {/* Taco AI */}
            <div>
              <h4 className="text-sm font-medium text-zinc-700 mb-3">Taco AI</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                  <div>
                    <Label className="text-sm">Taco Admin</Label>
                    <p className="text-xs text-zinc-500">View/edit Taco's memories</p>
                  </div>
                  <Switch checked={userPermissions.taco_admin} onCheckedChange={(checked) => setUserPermissions({ ...userPermissions, taco_admin: checked })} data-testid="perm-taco-admin" />
                </div>
                <div className="flex items-center justify-between py-2 border-b border-zinc-100">
                  <div>
                    <Label className="text-sm">Taco Conversation Review</Label>
                    <p className="text-xs text-zinc-500">Review all Taco conversations (default off)</p>
                  </div>
                  <Switch checked={userPermissions.taco_conversation_review} onCheckedChange={(checked) => setUserPermissions({ ...userPermissions, taco_conversation_review: checked })} data-testid="perm-taco-review" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleResetPermissions} disabled={creating} className="w-full sm:w-auto">
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset to {selectedUser?.role} Defaults
            </Button>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button variant="outline" onClick={() => setShowPermissionsDialog(false)}>Cancel</Button>
              <Button onClick={handleSavePermissions} disabled={creating} data-testid="save-permissions-btn">
                {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Permissions
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersTab;
